import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  ShieldCheckIcon,
  EyeIcon,
  TagIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import { Button, Input, Select, Card, Badge, Modal, ModalFooter, LoadingSpinner } from '@/components/common';
import api from '@/services/api';
import { useInsurerPermissions } from '@/hooks/useInsurerPermissions';
import {
  STATUS_BADGE,
  STATUS_LABELS,
  type ProductListRow,
  type ProductStatus,
} from '@/services/insurerProducts';
import { ProductHistory } from './ProductHistory';
import { ProductStatusDialog } from './ProductStatusDialog';
import toast from 'react-hot-toast';
import { formatCurrency } from '@/utils';

/**
 * #398: the insurer's Products (formerly "Insurance Policies"). Create and
 * edit happen on the full-page product form; status changes and History
 * (with one-click revert) live here and on the form. Controls follow the
 * caller's `products.*` permissions; the API enforces the same.
 */

// --- Types ---

type Policy = ProductListRow;

/** One connected broker's state for this product.
 *
 *  Access is allow-by-default, so there is usually NO BrokerPolicyAccess row: `id`
 *  is then null and `status` is 'AVAILABLE'. Only DISABLED_BY_BROKER /
 *  DISABLED_BY_INSURER take the product away.
 */
interface BrokerAccess {
  id: string | null;
  broker: string;
  broker_name: string;
  status: string;
  granted_at: string | null;
}

interface PaginatedPolicies {
  count: number;
  results: Policy[];
}

// --- Constants ---

const STATUS_FILTERS = [
  { value: '', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'discontinued', label: 'Discontinued' },
];

const CATEGORY_COLORS: Record<string, string> = {
  LIFE: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-200',
  HEALTH: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-200',
  MOTOR: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200',
  PROPERTY: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-200',
  TRAVEL: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-200',
  OTHER: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
};

const money = (value: string | null) => (value === null || value === '' ? null : formatCurrency(value));
const range = (lo: string | null, hi: string | null) => {
  const a = money(lo);
  const b = money(hi);
  if (a && b) return `${a} - ${b}`;
  return a || b || '-';
};

/** Status moves the list offers (the server enforces the same graph). */
function statusMoves(status: ProductStatus): { label: string; target: ProductStatus }[] {
  if (status === 'draft') return [{ label: 'Activate', target: 'active' }, { label: 'Discontinue', target: 'discontinued' }];
  if (status === 'active') return [{ label: 'Deactivate', target: 'inactive' }, { label: 'Discontinue', target: 'discontinued' }];
  if (status === 'inactive') return [{ label: 'Activate', target: 'active' }, { label: 'Discontinue', target: 'discontinued' }];
  return [];
}

// --- Component ---

export function PoliciesPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { can } = useInsurerPermissions();
  const canCreate = can('products.create');
  const canEdit = can('products.edit');
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailTab, setDetailTab] = useState<'details' | 'history'>('details');
  const [selectedPolicy, setSelectedPolicy] = useState<Policy | null>(null);
  const [statusChange, setStatusChange] = useState<{ policy: Policy; target: ProductStatus } | null>(null);

  // Fetch policies
  const { data, isLoading } = useQuery<PaginatedPolicies>({
    queryKey: ['insurance-policies', page, statusFilter, search],
    queryFn: async () => {
      const params: Record<string, string | number> = { page };
      if (statusFilter) params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      const res = await api.get('/insurers/insurance/policies/', { params });
      return res.data;
    },
  });

  // Fetch broker access for selected policy
  const { data: brokerAccess = [], isLoading: isLoadingAccess } = useQuery<BrokerAccess[]>({
    queryKey: ['insurance-policy-broker-access', selectedPolicy?.id],
    queryFn: async () => {
      const res = await api.get(`/insurers/insurance/policies/${selectedPolicy!.id}/broker_access/`);
      return res.data;
    },
    enabled: !!selectedPolicy && showDetailModal,
  });

  // The insurer's remaining per-broker lever: switch this product off for one
  // broker, or back on. (There is nothing to approve any more — product access is
  // allow-by-default, so brokers arrive already able to sell.)
  const toggleBrokerAccessMutation = useMutation({
    mutationFn: async ({ brokerId, enable }: { brokerId: string; enable: boolean }) => {
      const res = await api.post(
        `/insurers/insurance/policies/${selectedPolicy!.id}/toggle_broker_access/`,
        { broker_id: brokerId, enable },
      );
      return res.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['insurance-policy-broker-access', selectedPolicy?.id],
      });
      queryClient.invalidateQueries({ queryKey: ['insurer-product-versions', selectedPolicy?.id] });
      toast.success(variables.enable ? 'Enabled for broker' : 'Disabled for broker');
    },
    onError: () => toast.error('Failed to update broker access'),
  });

  const policies = data?.results ?? [];
  const totalItems = data?.count ?? 0;
  const totalPages = Math.ceil(totalItems / 12);

  const handleOpenDetail = (policy: Policy) => {
    setSelectedPolicy(policy);
    setDetailTab('details');
    setShowDetailModal(true);
  };

  const closeDetail = () => {
    setShowDetailModal(false);
    setSelectedPolicy(null);
  };

  return (
    <div>
      <PageHeader
        title="Products"
        subtitle="Your products as brokers see them. Changes go live when you save; every change can be reverted from History."
        breadcrumbs={[{ label: 'Insurance' }, { label: 'Products' }]}
        actions={
          canCreate && (
            <Button onClick={() => navigate('/insurance/policies/new')} leftIcon={<PlusIcon className="w-5 h-5" />}>
              Add Product
            </Button>
          )
        }
      />

      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Input
          aria-label="Search products"
          placeholder="Search name, code or UIN"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <Select
          aria-label="Filter by status"
          options={STATUS_FILTERS}
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        />
      </div>

      {/* Policies Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner />
        </div>
      ) : policies.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <ShieldCheckIcon className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-500" />
            <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-gray-100">No products found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {canCreate ? 'Get started by adding your first product.' : 'No products match.'}
            </p>
            {canCreate && (
              <Button
                className="mt-4"
                leftIcon={<PlusIcon className="w-4 h-4" />}
                onClick={() => navigate('/insurance/policies/new')}
              >
                Add Product
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {policies.map((policy) => (
              <div
                key={policy.id}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => handleOpenDetail(policy)}
              >
                <div className="p-5">
                  {/* Category Badge & Status */}
                  <div className="flex items-center justify-between mb-3">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        CATEGORY_COLORS[policy.category] ?? CATEGORY_COLORS.OTHER
                      }`}
                    >
                      <TagIcon className="w-3 h-3 mr-1" />
                      {policy.category}
                    </span>
                    <Badge variant={STATUS_BADGE[policy.status] ?? 'default'} size="sm">
                      {STATUS_LABELS[policy.status] ?? policy.status}
                    </Badge>
                  </div>

                  {/* Name & Code */}
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">{policy.name}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    {policy.code}
                    {policy.uin ? ` · UIN ${policy.uin}` : ''}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                    Premium {range(policy.min_premium, policy.max_premium)} · Sum insured{' '}
                    {range(policy.sum_insured_min, policy.sum_insured_max)}
                  </p>

                  {/* Description */}
                  {policy.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 line-clamp-2">{policy.description}</p>
                  )}

                  {/* Features (first 3) */}
                  {policy.features && policy.features.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {policy.features.slice(0, 3).map((feature, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                        >
                          {feature}
                        </span>
                      ))}
                      {policy.features.length > 3 && (
                        <span className="text-xs text-gray-400 dark:text-gray-500">+{policy.features.length - 3} more</span>
                      )}
                    </div>
                  )}

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {policy.is_visible_to_all ? 'Visible to all brokers' : 'Restricted access'}
                    </span>
                    <div className="flex items-center gap-1">
                      {canEdit &&
                        statusMoves(policy.status)
                          .slice(0, 1)
                          .map((move) => (
                            <button
                              key={move.target}
                              onClick={(e) => {
                                e.stopPropagation();
                                setStatusChange({ policy, target: move.target });
                              }}
                              className="rounded px-2 py-0.5 text-xs font-medium text-primary-700 hover:bg-gray-100 dark:text-primary-300 dark:hover:bg-gray-700"
                            >
                              {move.label}
                            </button>
                          ))}
                      {canEdit && policy.status !== 'discontinued' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/insurance/policies/${policy.id}/edit`);
                          }}
                          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-200"
                          title="Edit"
                          aria-label={`Edit ${policy.name}`}
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenDetail(policy);
                        }}
                        className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-200"
                        title="View Details"
                      >
                        <EyeIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Page {page} of {totalPages} ({totalItems} products)
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 1}>
                  Previous
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page >= totalPages}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Product Detail Modal */}
      <Modal isOpen={showDetailModal} onClose={closeDetail} title="Product Details" size="lg">
        {selectedPolicy && (
          <div role="tablist" aria-label="Product details" className="mb-4 flex gap-2">
            {(['details', 'history'] as const).map((key) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={detailTab === key}
                onClick={() => setDetailTab(key)}
                className={`rounded-full px-3 py-1 text-sm ${
                  detailTab === key
                    ? 'bg-primary-600 text-white dark:bg-primary-500 dark:text-white'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                }`}
              >
                {key === 'details' ? 'Details' : 'History'}
              </button>
            ))}
          </div>
        )}
        {selectedPolicy && detailTab === 'history' && (
          <ProductHistory productId={selectedPolicy.id} canEdit={canEdit} />
        )}
        {selectedPolicy && detailTab === 'details' && (
          <div className="space-y-5">
            {/* Policy Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Name</p>
                <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">{selectedPolicy.name}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Code</p>
                <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">{selectedPolicy.code}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Category</p>
                <span
                  className={`inline-flex items-center mt-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    CATEGORY_COLORS[selectedPolicy.category] ?? CATEGORY_COLORS.OTHER
                  }`}
                >
                  {selectedPolicy.category}
                </span>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</p>
                <div className="mt-1">
                  <Badge variant={STATUS_BADGE[selectedPolicy.status] ?? 'default'} size="sm">
                    {STATUS_LABELS[selectedPolicy.status] ?? selectedPolicy.status}
                  </Badge>
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">UIN</p>
                <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">{selectedPolicy.uin || '-'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Premium</p>
                <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">
                  {range(selectedPolicy.min_premium, selectedPolicy.max_premium)}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Sum insured</p>
                <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">
                  {range(selectedPolicy.sum_insured_min, selectedPolicy.sum_insured_max)}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Insurer</p>
                <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">{selectedPolicy.insurer_name}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Visibility</p>
                <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">
                  {selectedPolicy.is_visible_to_all ? 'All brokers' : 'Restricted'}
                </p>
              </div>
            </div>

            {/* Description */}
            {selectedPolicy.description && (
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">Description</p>
                <p className="text-sm text-gray-700 dark:text-gray-300">{selectedPolicy.description}</p>
              </div>
            )}

            {/* Features */}
            {selectedPolicy.features && selectedPolicy.features.length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">Features</p>
                <div className="flex flex-wrap gap-2">
                  {selectedPolicy.features.map((feature, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center px-2.5 py-1 rounded-md text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Broker Access List — every connected broker can sell this product by
                default; the only affordance is switching one off (or back on). */}
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">Broker Access</p>
              {isLoadingAccess ? (
                <div className="flex items-center justify-center py-4">
                  <LoadingSpinner />
                </div>
              ) : brokerAccess.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">No brokers are connected to you yet.</p>
              ) : (
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-900/40">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Broker</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Status</th>
                        <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {brokerAccess.map((access) => {
                        // No row, or a non-DISABLED_* row, means the broker can sell it.
                        const off = access.status.startsWith('DISABLED');
                        const byBroker = access.status === 'DISABLED_BY_BROKER';
                        return (
                          <tr key={access.broker}>
                            <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">{access.broker_name}</td>
                            <td className="px-4 py-2">
                              <Badge variant={off ? 'default' : 'success'} size="sm">
                                {off
                                  ? byBroker
                                    ? 'Disabled by broker'
                                    : 'Disabled by you'
                                  : 'Available'}
                              </Badge>
                            </td>
                            <td className="px-4 py-2 text-right">
                              {!canEdit ? null : byBroker ? (
                                // The broker switched it off for itself; re-enabling is
                                // theirs to do, not the insurer's.
                                <span className="text-xs text-gray-500 dark:text-gray-400">Broker&apos;s choice</span>
                              ) : (
                                <Button
                                  variant={off ? 'primary' : 'outline'}
                                  size="sm"
                                  isLoading={
                                    toggleBrokerAccessMutation.isPending &&
                                    toggleBrokerAccessMutation.variables?.brokerId === access.broker
                                  }
                                  onClick={() =>
                                    toggleBrokerAccessMutation.mutate({
                                      brokerId: access.broker,
                                      enable: off,
                                    })
                                  }
                                >
                                  {off ? 'Enable' : 'Disable'}
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
        <ModalFooter>
          <Button variant="outline" onClick={closeDetail}>
            Close
          </Button>
          {selectedPolicy &&
            canEdit &&
            statusMoves(selectedPolicy.status).map((move) => (
              <Button
                key={move.target}
                variant={move.target === 'discontinued' ? 'danger' : 'outline'}
                onClick={() => setStatusChange({ policy: selectedPolicy, target: move.target })}
              >
                {move.label}
              </Button>
            ))}
          {selectedPolicy && canEdit && selectedPolicy.status !== 'discontinued' && (
            <Button
              onClick={() => navigate(`/insurance/policies/${selectedPolicy.id}/edit`)}
              leftIcon={<PencilSquareIcon className="w-4 h-4" />}
            >
              Edit Product
            </Button>
          )}
        </ModalFooter>
      </Modal>

      {statusChange && (
        <ProductStatusDialog
          productId={statusChange.policy.id}
          productName={statusChange.policy.name}
          target={statusChange.target}
          onClose={() => {
            setStatusChange(null);
            closeDetail();
          }}
        />
      )}
    </div>
  );
}
